import React from 'react'
import { Icon } from '@blueprintjs/core'
import { capitalCase } from 'change-case'
import * as dotProp from 'dot-prop'
import { Params } from './useTableHooks'
import { Column, ExtraType } from '../../../types/table'
import { SelectionI } from '../../../types/constants'

export const DefaultSelected: SelectionI = {
    all: false,
    safeList: [],
    blockList: [],
    selectedCount: 0,
}

const tableHeaderIconColor = '#91969b'

export class Sort extends React.PureComponent<{ column: Column<any, any>; sort: string | null }> {
    render() {
        const { column, sort } = this.props

        if (typeof column !== 'string' && !column.sortable && !column.key) {
            return null
        }

        const sortDir = getSorting(column, sort)
        if (!sortDir) {
            return null
        }

        return (
            <Icon
                icon={sortDir === 'desc' ? 'caret-down' : 'caret-up'}
                color={tableHeaderIconColor}
                className="ml-2 shrink-0"
            />
        )
    }
}

export const getSorting = (column: Column<any, any>, sort: string | null): string | null => {
    if (typeof column !== 'string' && sort === column.key) {
        return 'asc'
    } else if (typeof column !== 'string' && sort === `-${column.key}`) {
        return 'desc'
    }

    return null
}

export const getHeader = (
    column: Column<any, any>,
    sort: string | null,
    setParams: any,
    loading: boolean = false,
    style: object = {},
): JSX.Element => {
    let header
    if (typeof column === 'string') {
        header = formatHeader(column)
    } else {
        header = column.title
            ? // @ts-ignore
              typeof column.title === 'function'
                ? column.title()
                : column.title
            : formatHeader(column.key?.toString() ?? null)
    }

    const sortable =
        column.key != '__select_row' &&
        typeof column !== 'string' &&
        column.key &&
        column.sortable !== false
    return (
        <div
            style={style}
            className={
                'd-flex p-2 ' + (sortable ? 'pointer ' : '') + (loading ? 'bp4-skeleton' : '')
            }
            onClick={() => handleHeaderClick(column, sort, setParams)}
        >
            {header}
            <Sort column={column} sort={sort} />
        </div>
    )
}

const formatHeader = (name: string | null): string => {
    if (!name) {
        return ''
    }

    return capitalCase(name.replace('_', ' '))
}

const handleHeaderClick = (
    column: Column<any, any>,
    sort: string | null,
    setParams?: any,
): void => {
    if (
        column.key != '__select_row' &&
        typeof column !== 'string' &&
        column.key &&
        column.sortable !== false
    ) {
        const dir = getSorting(column, sort)
        let sortDir: string | undefined = ''
        switch (dir) {
            case null:
                sortDir = `-${column.key}`
                break
            case 'desc':
                sortDir = column.key.toString()
                break
            case 'asc':
                sortDir = undefined
                break
        }

        if (setParams) {
            setParams((prev: Params) => ({ ...prev, sort: sortDir }))
        }
        console.log('Column clicked', column)
    }
}

export const stickyStyles = (
    stickyHeader: boolean,
    stickyFooter: boolean,
    stickyFooterOffset = 0,
): {
    stickyHeader: object | undefined
    stickyFooter: object | undefined
} => {
    return {
        stickyHeader: stickyHeader
            ? {
                  zIndex: 10,
                  top: 0,
                  position: 'sticky',
                  background: '#fff',
              }
            : undefined,
        stickyFooter: stickyFooter
            ? {
                  zIndex: 10,
                  bottom: stickyFooterOffset,
                  position: 'sticky',
                  background: '#fff',
                  transition: 'bottom 180ms ease-out',
              }
            : undefined,
    }
}

export const getCellValue = <T, Extra extends object>(
    column: Column<T, Extra>,
    item: T,
    extra: ExtraType & Extra,
): JSX.Element => {
    if (typeof column === 'object' && column.cell) {
        return column.cell(item, extra, false)
    }

    const key = typeof column === 'string' ? column : column.key
    // @ts-ignore
    let v = item[key]
    if (typeof key === 'string' && key.indexOf('.') !== -1) {
        v = dotProp.get(item, key)
    }

    if (typeof column !== 'string' && column.formatter) {
        return column.formatter(v)
    }

    return v
}
